import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';
import { 
    ArrowRightIcon, 
    CheckIcon,
    BookIcon,
    ClockIcon,
    SystemIcon,
    LevelIcon,
    WordsIcon,
    PracticeIcon,
    AdaptIcon,
    SupportIcon,
    ChartIcon,
    DocumentIcon,
    TrendingUpIcon,
    InfoIcon
} from './Icons';

export const LandingPage: React.FC = () => {
    const { language } = useLanguage();

    const processSteps = (t('process.steps', language) as any[]) || [];
    const changesPoints = (t('changes.points', language) as string[]) || [];

    const processIcons = [
        LevelIcon,
        WordsIcon,
        WordsIcon,
        PracticeIcon,
        AdaptIcon,
        SupportIcon
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold mb-8 leading-tight tracking-tight" style={{ color: '#324966' }}>
                        {t('hero.title', language) as string}
                    </h1>
                    <p className="text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed max-w-2xl">
                        {t('hero.subtitle', language) as string}
                    </p>
                    <a 
                        href="https://t.me/lingwisebot" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-base text-white transition-colors duration-150 hover:opacity-90"
                        style={{ backgroundColor: '#127BEB' }}
                    >
                        {t('hero.cta', language) as string}
                        <ArrowRightIcon className="w-4 h-4" />
                    </a>
                </div>
            </section>

            {/* Words Foundation Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="flex-shrink-0" style={{ color: '#127BEB' }}>
                            <BookIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: '#324966' }}>
                            {t('wordsFoundation.title', language) as string}
                        </h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        {t('wordsFoundation.description', language) as string}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        {t('wordsFoundation.highlight', language) as string}
                    </p>
                </div>
            </section>

            {/* Regular Work Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="flex-shrink-0" style={{ color: '#127BEB' }}>
                            <ClockIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: '#324966' }}>
                            {t('regularWork.title', language) as string}
                        </h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        {t('regularWork.description', language) as string}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        {t('regularWork.challenge', language) as string}
                    </p>
                </div>
            </section>

            {/* Organization Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="flex-shrink-0" style={{ color: '#127BEB' }}>
                            <SystemIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: '#324966' }}>
                            {t('organization.title', language) as string}
                        </h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                        {t('organization.description', language) as string}
                    </p>
                    
                    <div className="space-y-6">
                        <div className="border-l-2 pl-6" style={{ borderColor: '#127BEB' }}>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t('organization.timing', language) as string}
                            </p>
                        </div>
                        <div className="border-l-2 pl-6" style={{ borderColor: '#127BEB' }}>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t('organization.adaptation', language) as string}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-16 leading-tight" style={{ color: '#324966' }}>
                        {t('process.title', language) as string}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {processSteps.map((step: any, index: number) => {
                            const IconComponent = processIcons[index] || WordsIcon;
                            return (
                                <div
                                    key={index}
                                    className="bg-white rounded-lg p-6 border border-gray-200 hover:border-gray-300 transition-colors"
                                >
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="flex-shrink-0 mt-1" style={{ color: '#127BEB' }}>
                                            <IconComponent className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="mb-2">
                                                <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#324966' }}>
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                                {step.title}
                                            </h3>
                                            <p className="text-gray-600 leading-relaxed">
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Progress Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-12">
                        <div className="flex-shrink-0" style={{ color: '#127BEB' }}>
                            <ChartIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: '#324966' }}>
                            {t('progress.title', language) as string}
                        </h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                        {t('progress.description', language) as string}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        {t('progress.difficult', language) as string}
                    </p>
                </div>
            </section>

            {/* Texts Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="flex-shrink-0" style={{ color: '#127BEB' }}>
                            <DocumentIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: '#324966' }}>
                            {t('texts.title', language) as string}
                        </h2>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        {t('texts.description', language) as string}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        {t('texts.benefit', language) as string}
                    </p>
                </div>
            </section>

            {/* Changes Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-semibold mb-8 leading-tight" style={{ color: '#324966' }}>
                        {t('changes.title', language) as string}
                    </h2>
                    <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                        {t('changes.description', language) as string}
                    </p>
                    
                    <div className="mb-10">
                        <ul className="space-y-4">
                            {changesPoints.map((point: string, index: number) => (
                                <li key={index} className="flex items-start gap-3">
                                    <div style={{ color: '#127BEB' }}>
                                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                    </div>
                                    <span className="text-lg text-gray-700 leading-relaxed">{point}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="space-y-6">
                        <p className="text-lg text-gray-700 leading-relaxed">
                            {t('changes.result', language) as string}
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            {t('changes.understanding', language) as string}
                        </p>
                        <div className="flex items-center gap-3">
                            <div style={{ color: '#127BEB' }}>
                                <TrendingUpIcon className="w-6 h-6" />
                            </div>
                            <p className="text-lg font-medium leading-relaxed" style={{ color: '#324966' }}>
                                {t('changes.active', language) as string}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Important Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-3xl mx-auto">
                    <div className="border-l-2 pl-6" style={{ borderColor: '#324966' }}>
                        <div className="flex items-center gap-3 mb-3">
                            <div style={{ color: '#127BEB' }}>
                                <InfoIcon className="w-5 h-5" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900">
                                {t('important.title', language) as string}
                            </h3>
                        </div>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            {t('important.description', language) as string}
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-32 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-4xl sm:text-5xl font-semibold mb-6 leading-tight" style={{ color: '#324966' }}>
                        {t('finalCta.title', language) as string}
                    </h2>
                    <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                        {t('finalCta.description', language) as string}
                    </p>
                    <a 
                        href="https://t.me/lingwisebot" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-medium text-lg text-white transition-colors duration-150 hover:opacity-90"
                        style={{ backgroundColor: '#127BEB' }}
                    >
                        {t('finalCta.button', language) as string}
                        <ArrowRightIcon className="w-5 h-5" />
                    </a>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="text-sm text-gray-500 mb-4">
                        &copy; 2024 Lingwise. {language === 'ru' ? 'Все права защищены.' : 'All rights reserved.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-gray-500">
                        <Link 
                            to="/pricing" 
                            className="text-gray-600 hover:text-gray-900 underline transition-colors"
                        >
                            {language === 'ru' ? 'Тарифы' : 'Pricing'}
                        </Link>
                        <span className="hidden sm:inline text-gray-400">|</span>
                        <Link 
                            to="/terms" 
                            className="text-gray-600 hover:text-gray-900 underline transition-colors"
                        >
                            {language === 'ru' ? 'Пользовательское соглашение' : 'Terms of Service'}
                        </Link>
                        <span className="hidden sm:inline text-gray-400">|</span>
                        <Link 
                            to="/privacy" 
                            className="text-gray-600 hover:text-gray-900 underline transition-colors"
                        >
                            {language === 'ru' ? 'Политика конфиденциальности' : 'Privacy Policy'}
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};
